import * as Config from './config.js';
import { WorldProxy } from './WorldProxy.js';
import { EventBus, EVENTS } from '../services/EventBus.js';
import * as PersistenceService from '../services/PersistenceService.js';
import * as Symmetry from './Symmetry.js';
import { RulesetService } from './RulesetService.js';
import { AutoExploreService } from './AutoExploreService.js';
import { NativeTrajectoryModelService } from '../services/NativeTrajectoryModelService.js';
import { TrajectoryCaptureService } from '../services/TrajectoryCaptureService.js';
import { ShareCodec } from '../services/ShareCodec.js';
import { encodeWorldCode } from './WorldCodec.js';
import { ThumbnailBakeService } from './ThumbnailBakeService.js';
import { ExploreSessionCoordinator } from './ExploreSessionCoordinator.js';
import { ScrubHistoryController } from './ScrubHistoryController.js';
import { SpacetimeCaptureController } from './SpacetimeCaptureController.js';
import { rulesetToHex, hexToRuleset, findHexagonsInNeighborhood, cellsToBase64, rulesetName, getGridCenterWorld } from '../utils/utils.js';
import { parseStateFile } from '../utils/stateFile.js';
import { stateLibraryService } from '../services/StateLibraryService.js';
import { createInitialCameraStates } from './cameraState.js';

export class WorldManager {
    constructor(sharedSettings = {}) {
        this.worlds = [];
        this.cameraStates = [];
        this.sharedSettings = sharedSettings;
        this.simulationController = null;
        this.brushController = null;
        this.selectedWorldIndex = sharedSettings.selectedWorldIndex ?? Config.DEFAULT_SELECTED_WORLD_INDEX;
        this.isGloballyPaused = true;
        // State-history scrub-back (selected world) is coordinated by ScrubHistoryController, which owns
        // the view position + isScrubbing; the select-world and pause paths call into it.
        this.scrubHistoryController = new ScrubHistoryController(this);
        // The same ring's GPU mirror (#40). Idle unless the spacetime projection is actually on screen.
        this.spacetimeCaptureController = new SpacetimeCaptureController(this);
        this.deterministic = PersistenceService.loadUISetting('deterministic', true); // Add this
        this._hoverAffectedIndicesSet = new Set();
        this.isEntropySamplingEnabled = PersistenceService.loadUISetting('entropySamplingEnabled', false);
        this.entropySampleRate = PersistenceService.loadUISetting('entropySampleRate', 10);
        this.symmetryData = Symmetry.precomputeSymmetryGroups();
        this.rulesetService = new RulesetService(this.symmetryData);
        this.worldSettings = [];
        this.initialDefaultRulesetHex = "";
        // Ruleset Library preview baking (borrow-and-restore on a scratch world). Owns the "currently
        // baking world" index that _handleProxyUpdate reads to suppress a bake's transient burst echoes.
        // Constructed before _initWorlds so the guard reference exists before any proxy update arrives.
        this.thumbnailBakeService = new ThumbnailBakeService(this);
        // Borrow-worlds session helper: auto-explore snapshot/restore, candidate application, render
        // captures, and the Analysis panel's on-demand measure. Constructed before the AutoExploreService
        // below so its lazily-invoked capture providers can resolve through this coordinator.
        this.exploreSessionCoordinator = new ExploreSessionCoordinator(this);
        this._initWorlds();
        this._initCameraStates(sharedSettings.camera);
        // #37 Stage 4B: the native trajectory model is the DEFAULT learned objective. Training stays
        // in the sibling HexLifeInterestModel project; Explorer only loads the local versioned
        // manifest + ONNX artifact and exports exact HXLT1 slices. "Statistical only" turns it off
        // and restores the byte-identical pre-model search.
        const exploreObjective = PersistenceService.loadUISetting('exploreObjective', 'native-beta');
        this.nativeTrajectoryModelService = new NativeTrajectoryModelService({
            enabled: exploreObjective !== 'statistical',
        });
        /** Model id the native descriptor archive is currently keyed to (see NATIVE_MODEL_STATUS_CHANGED). */
        this._nativeDescriptorModelId = null;
        if (this.nativeTrajectoryModelService.enabled) this.nativeTrajectoryModelService.ensureReady();
        this.trajectoryCaptureService = new TrajectoryCaptureService(this);
        // Auto-explore (Phase 4): generation loop + session gallery. Constructed after worlds exist;
        // it only references the proxies/ruleset service lazily once started. The thumbnail provider
        // (v2.6, F6) waits a couple of rAFs for the renderer to draw the world's final eval frame,
        // then grabs a small JPEG data URL — DI so the service stays renderer-free. The native model
        // provider is injected the same way, so the service never imports a worker or a manifest.
        this.autoExploreService = new AutoExploreService(this, {
            thumbnailProvider: (worldIndex) => this._captureExploreThumbnail(worldIndex),
            nativeModelProvider: this.nativeTrajectoryModelService,
        });
        this._setupEventListeners();
    }

    setControllerReferences(simulationController, brushController) {
        this.simulationController = simulationController;
        this.brushController = brushController;
    }

    _initWorlds = () => {
        const hasSharedSettings = this.sharedSettings.fromUrl;

        if (hasSharedSettings) {
            console.log("Applying shared settings from URL.");
            this.worldSettings = [];

            const sharedRulesets = this.sharedSettings.rulesets; 
            const singleRuleset = this.sharedSettings.rulesetHex; 
            const sharedInitialStates = this.sharedSettings.initialStates;
            const sharedDensities = this.sharedSettings.densities; // Backward compatibility
            const enabledMask = this.sharedSettings.enabledMask ?? 0b111111111;

            for (let i = 0; i < Config.NUM_WORLDS; i++) {
                const rulesetHex = sharedRulesets ? sharedRulesets[i] : (singleRuleset || Config.INITIAL_RULESET_CODE);
                const enabled = (enabledMask & (1 << i)) !== 0;

                // Determine initial state - prefer new format, fall back to legacy density format
                let initialState;
                if (sharedInitialStates && sharedInitialStates[i]) {
                    initialState = sharedInitialStates[i];
                } else if (sharedDensities && sharedDensities[i] !== undefined) {
                    // Backward compatibility: convert density to new format
                    initialState = {
                        mode: 'density',
                        params: { density: sharedDensities[i] }
                    };
                } else {
                    // Default fallback
                    initialState = {
                        mode: 'density',
                        params: { density: Config.DEFAULT_INITIAL_DENSITIES[i] ?? 0.5 }
                    };
                }

                this.worldSettings.push({
                    initialState: initialState,
                    enabled: enabled,
                    locked: false,
                    isParent: false,
                    rulesetHex: rulesetHex,
                    rulesetHistory: [rulesetHex],
                    rulesetFuture: []
                });
            }
            PersistenceService.saveWorldSettings(this.worldSettings);
            
            PersistenceService.saveRuleset(this.worldSettings[this.selectedWorldIndex].rulesetHex);
        } else {
            this.worldSettings = PersistenceService.loadWorldSettings();
            
            this.worldSettings.forEach(setting => {
                if (!setting.rulesetHistory) {
                    setting.rulesetHistory = [setting.rulesetHex];
                }
                if (!setting.rulesetFuture) {
                    setting.rulesetFuture = [];
                }
                setting.locked = !!setting.locked;
                setting.isParent = !!setting.isParent;
            });
            this.initialDefaultRulesetHex = PersistenceService.loadRuleset() || Config.INITIAL_RULESET_CODE;
        }

        const worldManagerCallbacks = {
            onUpdate: (worldIndex, _updateType) => this._handleProxyUpdate(worldIndex, _updateType),
            onInitialized: (worldIndex) => this._handleProxyInitialized(worldIndex)
        };

        // One base seed for the whole initial-load batch so that, in deterministic mode, worlds
        // sharing an initial-state config seed identically from first paint (no explicit Reset
        // required). Matches the per-reset seeding done by every other reset path via _getResetSeed.
        const initialBaseSeed = Date.now();

        for (let i = 0; i < Config.NUM_WORLDS; i++) {
            const settings = this.worldSettings[i] || {
                initialState: {
                    mode: 'density',
                    params: { density: Config.DEFAULT_INITIAL_DENSITIES[i] ?? 0.5 }
                },
                enabled: Config.DEFAULT_WORLD_ENABLED_STATES[i] ?? true,
                locked: false,
                isParent: false,
                rulesetHex: this.initialDefaultRulesetHex,
                rulesetHistory: [this.initialDefaultRulesetHex],
                rulesetFuture: []
            };

            const rulesetArray = hexToRuleset(settings.rulesetHex);

            const proxy = new WorldProxy(i, {
                config: { GRID_ROWS: Config.GRID_ROWS, GRID_COLS: Config.GRID_COLS, NUM_CELLS: Config.NUM_CELLS },
                initialState: settings.initialState, // Modify this
                enabled: settings.enabled,
                rulesetArray: rulesetArray,
                rulesetHex: settings.rulesetHex,
                speed: this.simulationController?.getSpeed() || 1,
                initialEntropySamplingEnabled: this.isEntropySamplingEnabled,
                initialEntropySampleRate: this.entropySampleRate,
                seed: this._getResetSeed(initialBaseSeed, i),
            }, worldManagerCallbacks);
            this.worlds.push(proxy);
        }
    }

    _addRulesetToHistory = (worldIndex, rulesetHex) => {
        const worldSetting = this.worldSettings[worldIndex];
        if (!worldSetting) return;

        if (worldSetting.rulesetHistory[worldSetting.rulesetHistory.length - 1] === rulesetHex) {
            return;
        }
        
        worldSetting.rulesetHistory.push(rulesetHex);

        if (worldSetting.rulesetHistory.length > Config.RULESET_HISTORY_SIZE) {
            worldSetting.rulesetHistory.shift();
        }

        if (worldSetting.rulesetFuture.length > 0) {
            worldSetting.rulesetFuture = [];
        }
        EventBus.dispatch(EVENTS.HISTORY_CHANGED, { worldIndex });
    }

    _handleProxyInitialized = (worldIndex) => {
        EventBus.dispatch(EVENTS.WORKER_INITIALIZED, { worldIndex });
        if (worldIndex === this.selectedWorldIndex) {
            this.dispatchSelectedWorldUpdates();
            // Record scrub-back history on the selected world only (bounded memory).
            this.worlds[worldIndex]?.setHistoryCapture(true);
        }
        if (!this.isGloballyPaused && this.worlds[worldIndex]?.getLatestStats().isEnabled) {
            this.worlds[worldIndex].startSimulation();
            this.worlds[worldIndex].setSpeed(this.simulationController.getSpeed());
        }
    }

    _handleProxyUpdate = (worldIndex, _updateType) => {
        const proxy = this.worlds[worldIndex];
        if (!proxy) return;

        // A thumbnail bake batch is borrowing this world: its bursts carry a transient ruleset/state,
        // so don't let those echoes clobber persisted worldSettings or fire UI updates for it. The
        // batch restores the world and dispatches its true state when it finishes.
        if (worldIndex === this.thumbnailBakeService.bakingWorldIndex) return;

        const stats = proxy.getLatestStats();

        if (stats.rulesetHex && stats.rulesetHex !== "Error" && this.worldSettings[worldIndex]?.rulesetHex !== stats.rulesetHex) {
            this.worldSettings[worldIndex].rulesetHex = stats.rulesetHex;
            PersistenceService.saveWorldSettings(this.worldSettings);
            if (worldIndex === this.selectedWorldIndex) {
                EventBus.dispatch(EVENTS.RULESET_CHANGED, stats.rulesetHex);
            }
            EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        }

        if (worldIndex === this.selectedWorldIndex) {
            EventBus.dispatch(EVENTS.WORLD_STATS_UPDATED, { ...stats, worldIndex });
        }
    }

    dispatchSelectedWorldUpdates = () => {
        const selectedProxy = this.worlds[this.selectedWorldIndex];
        if (selectedProxy && selectedProxy.isInitialized) {
            const stats = selectedProxy.getLatestStats();
            EventBus.dispatch(EVENTS.WORLD_STATS_UPDATED, { ...stats, worldIndex: this.selectedWorldIndex });

            if (stats.rulesetHex && stats.rulesetHex !== "Error" && this.worldSettings[this.selectedWorldIndex]?.rulesetHex !== stats.rulesetHex) {
                this.worldSettings[this.selectedWorldIndex].rulesetHex = stats.rulesetHex;
                PersistenceService.saveWorldSettings(this.worldSettings);
                EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
            }
            EventBus.dispatch(EVENTS.RULESET_CHANGED, this.worldSettings[this.selectedWorldIndex]?.rulesetHex);
        }
    }

    _setupEventListeners() {
        this.#setupSimulationControlHandlers();
        this.#setupRulesetCommandHandlers();
        this.#setupEditorCommandHandlers();
        this.#setupWorldStateCommandHandlers();
        this.#setupInputAndInteractionHandlers();
        this.#setupAutoExploreHandlers();
    }

    #setupAutoExploreHandlers() {
        EventBus.subscribe(EVENTS.COMMAND_START_AUTO_EXPLORE, (options) => this.autoExploreService.start(options || {}));
        EventBus.subscribe(EVENTS.COMMAND_STOP_AUTO_EXPLORE, (data) => this.autoExploreService.stop(data || {}));
        EventBus.subscribe(EVENTS.COMMAND_PAUSE_AUTO_EXPLORE, () => this.autoExploreService.pause());
        EventBus.subscribe(EVENTS.COMMAND_RESUME_AUTO_EXPLORE, () => this.autoExploreService.resume());
        EventBus.subscribe(EVENTS.COMMAND_CLEAR_AUTO_EXPLORE_GALLERY, () => this.autoExploreService.clearGallery());
        EventBus.subscribe(EVENTS.COMMAND_APPLY_EXPLORE_FIND, (data) => this.applyExploreFind(data?.find));
        EventBus.subscribe(EVENTS.COMMAND_RETEST_EXPLORE_FIND, (data) => this.autoExploreService.retestFind(data?.find));
        EventBus.subscribe(EVENTS.COMMAND_SET_NATIVE_MODEL_ENABLED, (data) => {
            const enabled = !!data?.enabled;
            PersistenceService.saveUISetting('exploreObjective', enabled ? 'native-beta' : 'statistical');
            this.nativeTrajectoryModelService.setEnabled(enabled);
        });
        // The descriptor archive is loaded in AutoExploreService's constructor, which runs long
        // before the manifest arrives — so it loads under a null model id and cannot self-invalidate.
        // Re-key it the moment the real id is known (and on any later id change). Refused mid-run:
        // the search owns the archive, and SimHash cells from two models are not comparable.
        EventBus.subscribe(EVENTS.NATIVE_MODEL_STATUS_CHANGED, (status) => {
            const modelId = status?.modelId || null;
            if (!modelId || modelId === this._nativeDescriptorModelId) return;
            if (this.autoExploreService.isRunning()) return;
            this._nativeDescriptorModelId = modelId;
            this.autoExploreService.onNativeModelChanged();
        });
        EventBus.subscribe(EVENTS.COMMAND_CAPTURE_TRAINING_SLICE, async (data) => {
            try {
                const result = await this.trajectoryCaptureService.captureAndDownload(data || {});
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                    message: `Exported ${result.header.frameCount}-frame training slice.`,
                    type: 'success',
                });
            } catch (error) {
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                    message: error?.message || 'Training-slice capture failed.',
                    type: 'error',
                });
            }
        });
        EventBus.subscribe(EVENTS.COMMAND_EVALUATE_NATIVE_MODEL, async (data) => {
            try {
                const result = await this.trajectoryCaptureService.evaluateSelected(
                    this.nativeTrajectoryModelService,
                    data || {},
                );
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                    message: `Native reward ${result.reward.toFixed(3)} · ${result.modelId}`,
                    type: 'info',
                    duration: 7000,
                });
            } catch (error) {
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                    message: error?.message || 'Native-model evaluation failed.',
                    type: 'error',
                });
            }
        });
    }

    /**
     * Apply a gallery find to the selected world so the user can study the discovered behavior:
     * set the world's ruleset (pushed to history — this is a deliberate adopt, unlike the search's
     * throwaway candidates) and reset with the find's winning initial condition + deterministic seed,
     * exactly reproducing the IC the score was won on. Stops any in-flight explore loop first.
     * @param {import('./analysis/BehaviorArchive.js').ArchiveEntry} find
     */
    applyExploreFind = (find) => {
        if (!find || !find.hex || find.hex === 'Error') return;
        if (this.autoExploreService?.isRunning()) this.autoExploreService.stop();

        const idx = this.selectedWorldIndex;
        const settings = this.worldSettings[idx];
        const proxy = this.worlds[idx];
        if (!settings || !proxy) return;

        if (find.initialState) settings.initialState = structuredClone(find.initialState);
        this._commitRuleset(idx, find.hex, {
            addToHistory: true,
            reset: true,
            seed: find.seed,
        });
        PersistenceService.saveRuleset(find.hex);
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.RULESET_CHANGED, find.hex);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        EventBus.dispatch(EVENTS.ALL_WORLDS_RESET);
        EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: `Applied "${find.mnemonic || rulesetName(find.hex)}" (${find.icLabel || 'IC'}).`, type: 'success' });
    };

    #setupSimulationControlHandlers() {
        EventBus.subscribe(EVENTS.SIMULATION_PAUSED, this.setGlobalPause);
        EventBus.subscribe(EVENTS.COMMAND_SET_SPEED, (speed) => this.setGlobalSpeed(speed));
        EventBus.subscribe(EVENTS.COMMAND_SELECT_WORLD, (newIndex) => {
            const prevIndex = this.selectedWorldIndex;
            if (newIndex !== prevIndex) {
                this.scrubHistoryController.handleWorldSelected(prevIndex, newIndex);
            }
            this.selectedWorldIndex = newIndex;
            this.dispatchSelectedWorldUpdates();
            EventBus.dispatch(EVENTS.SELECTED_WORLD_CHANGED, newIndex);
            this.scrubHistoryController.dispatchScrubState();
        });
        EventBus.subscribe(EVENTS.COMMAND_SCRUB_HISTORY, (data) => this.scrubHistoryController.scrubSelectedHistory(data?.offset ?? 0));
        EventBus.subscribe(EVENTS.COMMAND_STATE_STEP, (data) => this.scrubHistoryController.stepSelectedHistory(data?.delta ?? 0));
        EventBus.subscribe(EVENTS.COMMAND_EXIT_SCRUB, () => this.scrubHistoryController.exitScrub());
        EventBus.subscribe(EVENTS.COMMAND_SET_ENTROPY_SAMPLING, (data) => {
            this.isEntropySamplingEnabled = data.enabled;
            this.entropySampleRate = data.rate;
            PersistenceService.saveUISetting('entropySamplingEnabled', data.enabled);
            PersistenceService.saveUISetting('entropySampleRate', data.rate);
            this.worlds.forEach(proxy => proxy.sendCommand('SET_ENTROPY_SAMPLING_PARAMS', {
                enabled: this.isEntropySamplingEnabled,
                rate: this.entropySampleRate
            }));
            EventBus.dispatch(EVENTS.ENTROPY_SAMPLING_CHANGED, { enabled: this.isEntropySamplingEnabled, rate: this.entropySampleRate });
        });
        
        // --- ADD THIS ---
        EventBus.subscribe(EVENTS.COMMAND_SET_DETERMINISTIC_RESET, (isDeterministic) => {
            this.deterministic = isDeterministic;
            PersistenceService.saveUISetting('deterministic', isDeterministic);
        });
    }

    #setupRulesetCommandHandlers() {
        EventBus.subscribe(EVENTS.COMMAND_GENERATE_RANDOM_RULESET, (data) => {
            const newRulesetHex = this._generateRandomRulesetHex(data.bias, data.generationMode);
            this.#applyRulesetToWorlds(newRulesetHex, data.applyScope, data.shouldReset, true);
        });
        EventBus.subscribe(EVENTS.COMMAND_SET_RULESET, (data) => {
            this.#applyRulesetToWorlds(data.hexString, data.scope, data.resetOnNewRule);
        });
        EventBus.subscribe(EVENTS.COMMAND_MUTATE_RULESET, (data) => {
            this._mutateRulesetForScope(data.scope, data.mutationRate, data.mode);
        });
        EventBus.subscribe(EVENTS.COMMAND_CLONE_AND_MUTATE, (data) => {
            this._cloneAndMutateOthers(data.mutationRate, data.mode, data.ensureMutation);
        });
        EventBus.subscribe(EVENTS.COMMAND_CLONE_RULESET, () => {
            this._cloneRuleset();
        });
        EventBus.subscribe(EVENTS.COMMAND_BREED_WORLDS, (data) => {
            this._breedFromGenepool(data?.mode, data?.postMutationRate);
        });
        EventBus.subscribe(EVENTS.COMMAND_INVERT_RULESET, this._invertSelectedRuleset);
        EventBus.subscribe(EVENTS.COMMAND_UNDO_RULESET, (data) => this.undoRulesetChange(data.worldIndex));
        EventBus.subscribe(EVENTS.COMMAND_REDO_RULESET, (data) => this.redoRulesetChange(data.worldIndex));
        EventBus.subscribe(EVENTS.COMMAND_REVERT_TO_HISTORY_STATE, (data) => this.revertToHistoryState(data.worldIndex, data.historyIndex));
    }

    #setupEditorCommandHandlers() {
        EventBus.subscribe(EVENTS.COMMAND_EDITOR_TOGGLE_RULE_OUTPUT, (data) => {
            this._modifyRulesetForScope(data.modificationScope, (currentRulesetHex) => {
                const rules = hexToRuleset(currentRulesetHex);
                if (data.ruleIndex >= 0 && data.ruleIndex < 128) {
                    rules[data.ruleIndex] = 1 - rules[data.ruleIndex];
                }
                return rulesetToHex(rules);
            }, data.conditionalResetScope);
        });
        EventBus.subscribe(EVENTS.COMMAND_EDITOR_SET_ALL_RULES_STATE, (data) => {
            this._modifyRulesetForScope(data.modificationScope, (currentRulesetHex) => {
                const rules = hexToRuleset(currentRulesetHex);
                rules.fill(data.targetState);
                return rulesetToHex(rules);
            }, data.conditionalResetScope);
        });
        EventBus.subscribe(EVENTS.COMMAND_EDITOR_SET_RULES_FOR_NEIGHBOR_COUNT, (data) => {
            this._modifyRulesetForScope(data.modificationScope, (currentRulesetHex) => {
                const rules = hexToRuleset(currentRulesetHex);
                for (let mask = 0; mask < 64; mask++) {
                    if (Symmetry.countSetBits(mask) === data.numActive) {
                        const idx = (data.centerState << 6) | mask;
                        rules[idx] = data.outputState;
                    }
                }
                return rulesetToHex(rules);
            }, data.conditionalResetScope);
        });
        EventBus.subscribe(EVENTS.COMMAND_EDITOR_SET_RULES_FOR_TOTALISTIC_SUM, (data) => {
            this._modifyRulesetForScope(data.modificationScope, (currentRulesetHex) => {
                const rules = hexToRuleset(currentRulesetHex);
                RulesetService.setRulesForTotalisticSum(rules, data.sum, data.outputState);
                return rulesetToHex(rules);
            }, data.conditionalResetScope);
        });
        EventBus.subscribe(EVENTS.COMMAND_EDITOR_SET_RULES_FOR_CANONICAL_REPRESENTATIVE, (data) => {
            this._modifyRulesetForScope(data.modificationScope, (currentRulesetHex) => {
                const rules = hexToRuleset(currentRulesetHex);
                const groups = data.symmetryMode === 'd_sym'
                    ? this.symmetryData.dihedralCanonicalRepresentatives
                    : this.symmetryData.canonicalRepresentatives;
                const group = groups.find(g => g.representative === data.canonicalBitmask);
                if (group) {
                    for (const member of group.members) {
                        const idx = (data.centerState << 6) | member;
                        rules[idx] = data.outputState;
                    }
                }
                return rulesetToHex(rules);
            }, data.conditionalResetScope);
        });
        EventBus.subscribe(EVENTS.COMMAND_EDITOR_SET_RULESET_HEX, (data) => {
            const shouldReset = data.conditionalResetScope !== 'none';
            this.#applyRulesetToWorlds(data.hexString, data.modificationScope, shouldReset);
        });
    }
    
    #setupWorldStateCommandHandlers() {
        EventBus.subscribe(EVENTS.COMMAND_APPLY_SELECTED_INITIAL_STATE_TO_ALL, this._applySelectedInitialStateToAll);
        EventBus.subscribe(EVENTS.COMMAND_RESET_INITIAL_STATES_TO_DEFAULT, this._resetStatesToDefault);
        EventBus.subscribe(EVENTS.COMMAND_RESET_ALL_WORLDS_TO_INITIAL_DENSITIES, () => {
            this._guardDestructive({
                title: 'Reset all 9 worlds?',
                message: 'Re-seed every world with fresh random cells at its starting density. This affects all nine worlds and cannot be undone.',
                confirmLabel: 'Reset all',
                run: () => this._resetAllWorldsToInitialDensities(),
            });
        });
        EventBus.subscribe(EVENTS.COMMAND_RESET_WORLDS_WITH_CURRENT_RULESET, (data) => {
            const primaryRulesetHex = this.worldSettings[this.selectedWorldIndex]?.rulesetHex;
            if (!primaryRulesetHex) {
                console.error("Cannot reset with current ruleset: Selected world's ruleset is undefined.");
                return;
            }
            const indicesToReset = this._getAffectedWorldIndices(data.scope);
            const baseSeed = Date.now();
            indicesToReset.forEach(idx => {
                if (data.copyPrimaryRuleset && idx !== this.selectedWorldIndex) {
                    // Copy-on-reset doesn't push history (it isn't a user ruleset edit).
                    this._commitRuleset(idx, primaryRulesetHex, { addToHistory: false });
                }
                if (this.worldSettings[idx]) {
                    this.worlds[idx].resetWorld(this.worldSettings[idx].initialState, this._getResetSeed(baseSeed, idx));
                    if (!this.isGloballyPaused && this.worldSettings[idx].enabled) this.worlds[idx].startSimulation();
                }
            });
            if (data.scope === 'all' || indicesToReset.includes(this.selectedWorldIndex)) {
                EventBus.dispatch(EVENTS.ALL_WORLDS_RESET);
            }
            this.dispatchSelectedWorldUpdates();
            PersistenceService.saveWorldSettings(this.worldSettings);
        });
        EventBus.subscribe(EVENTS.COMMAND_CLEAR_WORLDS, (data) => {
            // Only the all-worlds clear is unrecoverable; a single world keeps scrub-back,
            // so it bypasses the confirmation gate.
            if (data?.scope === 'all') {
                this._guardDestructive({
                    title: 'Clear all 9 worlds?',
                    message: 'Empty every cell to the inactive state across all nine worlds. This cannot be undone.',
                    confirmLabel: 'Clear all',
                    run: () => this._clearWorlds(data),
                });
            } else {
                this._clearWorlds(data);
            }
        });
        EventBus.subscribe(EVENTS.COMMAND_SAVE_SELECTED_WORLD_STATE, this.saveSelectedWorldState);
        EventBus.subscribe(EVENTS.COMMAND_LOAD_WORLD_STATE, (data) => this.loadWorldState(data.worldIndex, data.loadedData));
        EventBus.subscribe(EVENTS.COMMAND_TOGGLE_WORLD_LOCK, () => this.toggleSelectedWorldLock());
        EventBus.subscribe(EVENTS.COMMAND_TOGGLE_WORLD_PARENT, (data) => this.toggleWorldParent(data?.worldIndex));
        EventBus.subscribe(EVENTS.COMMAND_COPY_WORLD_STATE, (data) => this.copyWorldState(this.selectedWorldIndex, data.targetWorldIndex));
        EventBus.subscribe(EVENTS.COMMAND_CAPTURE_STATE_TO_LIBRARY, (data) => this.captureStateToLibrary(data?.assignScope ?? 'selected'));
        EventBus.subscribe(EVENTS.COMMAND_SET_WORLD_INITIAL_STATE, (data) => {
            if (this.worldSettings[data.worldIndex]) {
                this.worldSettings[data.worldIndex].initialState = data.initialState;
                PersistenceService.saveWorldSettings(this.worldSettings);
                EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
            }
        });
        EventBus.subscribe(EVENTS.COMMAND_SET_WORLD_ENABLED, (data) => {
            if (this.worldSettings[data.worldIndex] && this.worlds[data.worldIndex]) {
                this.worldSettings[data.worldIndex].enabled = data.isEnabled;
                this.worlds[data.worldIndex].setEnabled(data.isEnabled);
                if (data.isEnabled && !this.isGloballyPaused) {
                    this.worlds[data.worldIndex].startSimulation();
                    this.worlds[data.worldIndex].setSpeed(this.simulationController.getSpeed());
                } else if (!data.isEnabled) {
                    this.worlds[data.worldIndex].stopSimulation();
                }
                PersistenceService.saveWorldSettings(this.worldSettings);
                EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
            }
        });
    }
    
    #setupInputAndInteractionHandlers() {
        EventBus.subscribe(EVENTS.COMMAND_APPLY_BRUSH, (data) => {
            this.worlds[data.worldIndex]?.applyBrush(data.col, data.row, this.brushController.getBrushSize());
        });
        EventBus.subscribe(EVENTS.COMMAND_APPLY_SELECTIVE_BRUSH, (data) => {
            this.worlds[data.worldIndex]?.applySelectiveBrush(data.cellIndices, data.brushMode);
        });
        EventBus.subscribe(EVENTS.COMMAND_SHIFT_WORLD, (data) => {
            const idx = (data && typeof data.worldIndex === 'number') ? data.worldIndex : this.selectedWorldIndex;
            this.worlds[idx]?.shiftState(data.dCol | 0, data.dRow | 0);
        });
        EventBus.subscribe(EVENTS.COMMAND_SET_HOVER_STATE, (data) => {
            findHexagonsInNeighborhood(data.col, data.row, this.brushController.getBrushSize(), this._hoverAffectedIndicesSet);
            this.worlds[data.worldIndex]?.setHoverState(this._hoverAffectedIndicesSet);
        });
        EventBus.subscribe(EVENTS.COMMAND_CLEAR_HOVER_STATE, (data) => {
            this.worlds[data.worldIndex]?.clearHoverState();
        });
        EventBus.subscribe(EVENTS.COMMAND_UPDATE_GHOST_PREVIEW, (data) => {
            this.worlds[this.selectedWorldIndex]?.setGhostState(data.indices);
        });
        EventBus.subscribe(EVENTS.COMMAND_CLEAR_GHOST_PREVIEW, () => {
            this.worlds[this.selectedWorldIndex]?.clearGhostState();
        });
    }

    _applySelectedInitialStateToAll = () => {
        const selectedState = this.worldSettings[this.selectedWorldIndex]?.initialState;
        if (!selectedState) return;
        const stateCopy = structuredClone(selectedState);
        this.worldSettings.forEach(setting => {
            setting.initialState = structuredClone(stateCopy);
        });
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
    }

    _resetStatesToDefault = () => {
        this.worldSettings.forEach((setting, idx) => {
            setting.initialState = {
                mode: 'density',
                params: { density: Config.DEFAULT_INITIAL_DENSITIES[idx] ?? 0.5 }
            };
        });
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
    }

    /** True when a world's ruleset is locked against evolutionary/automatic rewrites (Generate,
     *  Mutate, Clone, Clone & Mutate, Breed). Deliberate sets/edits ignore this. */
    _isLocked = (idx) => !!this.worldSettings[idx]?.locked;

    /** True when a world is flagged as a breeding parent (a member of the genepool). */
    _isParent = (idx) => !!this.worldSettings[idx]?.isParent;

    /** Enabled worlds currently flagged as breeding parents (the genepool). */
    _getParentIndices = () =>
        this.worldSettings.reduce((acc, ws, idx) => {
            if (ws?.isParent && ws?.enabled) acc.push(idx);
            return acc;
        }, []);

    _getAffectedWorldIndices = (scope) => {
        if (scope === 'none') return [];
        if (scope === 'all') return this.worlds.map((_, i) => i);
        if (scope === 'selected') return [this.selectedWorldIndex];
        if (typeof scope === 'number' && scope >= 0 && scope < this.worlds.length) return [scope];
        console.warn("Invalid scope for _getAffectedWorldIndices:", scope);
        return [];
    }

    /**
     * Computes the RNG seed for a single world reset. In deterministic mode every
     * world shares `baseSeed`, so worlds with matching initial-state configs reset
     * to identical grids; otherwise each world is offset by its index. Callers must
     * capture `baseSeed` once (e.g. `Date.now()`) before the reset loop so a single
     * operation seeds all worlds consistently.
     * @param {number} baseSeed - A single seed captured once per reset operation.
     * @param {number} worldIndex
     * @returns {number}
     */
    _getResetSeed(baseSeed, worldIndex) {
        return this.deterministic ? baseSeed : baseSeed + worldIndex;
    }

    /**
     * Run a destructive all-worlds action, optionally behind a confirmation dialog. The
     * "confirm destructive actions" preference (default on) is read live; when off, the
     * action runs immediately. Routing through the EventBus keeps WorldManager UI-agnostic
     * and gates every dispatch source (toolbar popout, FABs, keyboard, command palette) at
     * once. The confirmation is requested via COMMAND_SHOW_CONFIRMATION; `run` performs the
     * work directly on confirm (no command re-dispatch, so no loop).
     * @param {{title:string, message:string, confirmLabel:string, run:()=>void}} opts
     */
    _guardDestructive({ title, message, confirmLabel, run }) {
        if (PersistenceService.loadUISetting('confirmDestructiveActions', true)) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_CONFIRMATION, { title, message, confirmLabel, onConfirm: run });
        } else {
            run();
        }
    }

    /** Re-seed all worlds with fresh random cells at their configured densities. */
    _resetAllWorldsToInitialDensities() {
        const baseSeed = Date.now();
        this.worlds.forEach((proxy, idx) => {
            if (this.worldSettings[idx]) {
                proxy.resetWorld(this.worldSettings[idx].initialState, this._getResetSeed(baseSeed, idx));
                if (!this.isGloballyPaused && this.worldSettings[idx].enabled) proxy.startSimulation();
            }
        });
        EventBus.dispatch(EVENTS.ALL_WORLDS_RESET);
    }

    /** Empty cells to the inactive state across the scope's worlds (clear, not reset). */
    _clearWorlds(data) {
        const indicesToClear = this._getAffectedWorldIndices(data.scope);
        indicesToClear.forEach(idx => {
            const proxy = this.worlds[idx];
            if (!proxy) return;

            const initialState = this.worldSettings[idx]?.initialState;
            if (!initialState) {
                console.warn(`World ${idx} has no initial state, skipping clear.`);
                return;
            }
            const clearStateConfig = { ...initialState, params: { ...initialState.params, density: 0 } };
            proxy.sendCommand('RESET_WORLD', { isClearOperation: true, initialState: clearStateConfig });
        });

        if (data.scope === 'all') EventBus.dispatch(EVENTS.ALL_WORLDS_RESET);
    }

    /**
     * Apply a ruleset to a single world, centralizing the push-history → hexToRuleset →
     * buffer.slice(0) → setRuleset → update-worldSettings dance repeated across every
     * ruleset-mutation path. Callers still own scope resolution, persistence, and event
     * dispatch (so a batch can persist/dispatch once after the loop).
     * @param {number} worldIndex
     * @param {string} hex - 32-char ruleset hex (a falsy world/settings entry is a no-op).
     * @param {object} [opts]
     * @param {boolean} [opts.addToHistory=true] - Push hex onto the world's ruleset history.
     *   Set false for undo/redo/revert, which reshape history themselves.
     * @param {boolean} [opts.uploadToWorker=true] - Send the parsed rule buffer to the worker.
     *   Set false when the worker already has this ruleset (clone source) or receives it via
     *   another command (LOAD_STATE). Skipped when hex is "Error".
     * @param {boolean} [opts.reset=false] - Reset the world's grid after applying.
     * @param {number} [opts.seed] - Reset seed (used when reset is true).
     */
    _commitRuleset = (worldIndex, hex, opts = {}) => {
        const { addToHistory = true, uploadToWorker = true, reset = false, seed } = opts;
        const proxy = this.worlds[worldIndex];
        const settings = this.worldSettings[worldIndex];
        if (!proxy || !settings) return;

        if (addToHistory) this._addRulesetToHistory(worldIndex, hex);
        if (uploadToWorker && hex !== "Error") {
            proxy.setRuleset(hexToRuleset(hex).buffer.slice(0));
        }
        settings.rulesetHex = hex;
        if (reset) proxy.resetWorld(settings.initialState, seed);
    }

    #applyRulesetToWorlds = (rulesetHex, scope, shouldReset, respectLocks = false) => {
        const newRulesetArray = hexToRuleset(rulesetHex);
        if (rulesetHex === "Error" || newRulesetArray.length !== 128) {
            console.error("Cannot apply invalid ruleset hex:", rulesetHex);
            return;
        }

        let indicesToAffect = this._getAffectedWorldIndices(scope);
        if (respectLocks) indicesToAffect = indicesToAffect.filter(idx => !this._isLocked(idx));
        const baseSeed = Date.now();

        indicesToAffect.forEach(idx => {
            this._commitRuleset(idx, rulesetHex, {
                reset: shouldReset,
                seed: this._getResetSeed(baseSeed, idx),
            });
        });

        if (indicesToAffect.includes(this.selectedWorldIndex)) {
            EventBus.dispatch(EVENTS.RULESET_CHANGED, rulesetHex);
            PersistenceService.saveRuleset(rulesetHex);
        }
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
    }

    // Delegates to RulesetService. n_count mode seeds its flips from the *selected* world's
    // effective per-neighbor-count output (preserving the prior behaviour), hence the parsed
    // current ruleset is passed as the reference.
    _generateMutatedHex = (sourceHex, mutationRate, mutationMode) =>
        this.rulesetService.generateMutatedHex(sourceHex, mutationRate, mutationMode, this._getParsedCurrentRuleset());

    _mutateRulesetForScope = (scope, mutationRate, mutationMode = 'single') => {
        const indices = this._getAffectedWorldIndices(scope);

        indices.forEach(idx => {
            if (this._isLocked(idx)) return;
            const proxyStats = this.worlds[idx]?.getLatestStats();
            let currentHex = (proxyStats?.rulesetHex && proxyStats.rulesetHex !== "Error")
                ? proxyStats.rulesetHex
                : this.worldSettings[idx]?.rulesetHex;

            if (!currentHex) {
                console.warn(`_mutateRulesetForScope: No current hex for world ${idx}, skipping mutation.`);
                return;
            }

            const newHex = this._generateMutatedHex(currentHex, mutationRate, mutationMode);

            if (newHex !== currentHex && newHex !== "Error") {
                this._commitRuleset(idx, newHex);
            }
        });

        if (indices.includes(this.selectedWorldIndex)) {
            this.dispatchSelectedWorldUpdates();
            if (this.worldSettings[this.selectedWorldIndex]) {
                PersistenceService.saveRuleset(this.worldSettings[this.selectedWorldIndex].rulesetHex);
            }
        }
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
    };

    _cloneAndMutateOthers = (mutationRate, mutationMode = 'single', ensureMutation = false) => {
        const selectedProxy = this.worlds[this.selectedWorldIndex];
        if (!selectedProxy) {
            console.error("Cannot clone/mutate: selected world proxy is not available.");
            return;
        }

        const sourceRulesetHex = this.getCurrentRulesetHex();

        if (sourceRulesetHex === "Error" || sourceRulesetHex === "N/A") {
             console.error("Cannot clone/mutate: selected world's ruleset is invalid.");
             EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: "Selected world has an invalid ruleset and cannot be cloned.", type: 'error' });
             return;
        }

        const generatedHexes = new Set([sourceRulesetHex]);
        const baseSeed = Date.now();

        this.worlds.forEach((proxy, idx) => {
            let newHex = sourceRulesetHex;
            const isSelected = idx === this.selectedWorldIndex;
            // A locked world keeps its ruleset (and grid) untouched by clone & mutate.
            if (!isSelected && this._isLocked(idx)) return;
            if (!isSelected) {
                let attempts = 0;
                const maxAttempts = 10; // prevent infinite loop
                do {
                    newHex = this._generateMutatedHex(sourceRulesetHex, mutationRate, mutationMode);
                    attempts++;
                } while (ensureMutation && generatedHexes.has(newHex) && attempts < maxAttempts);

                if (ensureMutation && generatedHexes.has(newHex) && attempts >= maxAttempts) {
                    console.warn(`Could not generate a unique mutation for ${sourceRulesetHex} after ${maxAttempts} attempts. Forcing one.`);
                    // Force a single bit flip if still no mutation
                    const rules = hexToRuleset(newHex);
                    let forcedHex;
                    let forceAttempts = 0;
                    do {
                        const randomIndex = Math.floor(Math.random() * 128);
                        rules[randomIndex] = 1 - rules[randomIndex];
                        forcedHex = rulesetToHex(rules);
                        forceAttempts++;
                    } while (generatedHexes.has(forcedHex) && forceAttempts < 128);
                    newHex = forcedHex;
                }

                if (newHex !== "Error") generatedHexes.add(newHex);
            }

            // The selected world already runs the source ruleset, so skip its worker upload.
            this._commitRuleset(idx, newHex, {
                uploadToWorker: !isSelected,
                reset: true,
                seed: this._getResetSeed(baseSeed, idx),
            });

            if (!this.isGloballyPaused) {
                proxy.startSimulation();
            }
        });


        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        EventBus.dispatch(EVENTS.ALL_WORLDS_RESET);
    }

    /** The current ruleset hex of a world by index (live worker stats first, then saved settings). */
    _getRulesetHexForWorld = (idx) => {
        const stats = this.worlds[idx]?.getLatestStats();
        if (stats?.rulesetHex && stats.rulesetHex !== "Error") return stats.rulesetHex;
        return this.worldSettings[idx]?.rulesetHex || null;
    };

    /**
     * Breed from the genepool: every enabled world flagged as a parent (`isParent`) is a source; each
     * remaining enabled, non-parent, non-locked world receives a fresh `crossoverPoolHexes(...)` child
     * recombined from the pool. Parents keep their rulesets. Offspring are reset+restarted so the
     * recombination is visible immediately.
     * - 0 parents → no-op (with guidance toast).
     * - 1 parent  → each offspring is that parent's ruleset + post-mutation (i.e. clone-and-mutate).
     * - ≥2        → multi-parent recombination (2 parents is identical to the old A×B breed).
     * @param {'uniform'|'r_sym'|'d_sym'|'n_count'|'totalistic'} [mode='r_sym']
     * @param {number} [postMutationRate=0]
     */
    _breedFromGenepool = (mode = 'r_sym', postMutationRate = 0) => {
        const parentIndices = this._getParentIndices();
        if (parentIndices.length === 0) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: "Flag at least one world as a parent first (press B).", type: 'error' });
            return;
        }

        const parentHexes = parentIndices
            .map(idx => this._getRulesetHexForWorld(idx))
            .filter(hex => hex && hex !== "N/A" && hex !== "Error");
        if (parentHexes.length === 0) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: "Cannot breed: parent worlds have invalid rulesets.", type: 'error' });
            return;
        }

        // Offspring slots: enabled, not a parent, not locked.
        const offspringIndices = [];
        this.worldSettings.forEach((ws, idx) => {
            if (ws?.enabled && !this._isParent(idx) && !this._isLocked(idx)) offspringIndices.push(idx);
        });
        if (offspringIndices.length === 0) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: "No offspring worlds — leave an enabled, unlocked, non-parent world to breed into.", type: 'error' });
            return;
        }

        const baseSeed = Date.now();
        offspringIndices.forEach(idx => {
            const childHex = this.rulesetService.crossoverPoolHexes(parentHexes, mode, Math.random, postMutationRate);
            if (!childHex || childHex === "Error") return;
            this._commitRuleset(idx, childHex, {
                uploadToWorker: true,
                reset: true,
                seed: this._getResetSeed(baseSeed, idx),
            });
            if (!this.isGloballyPaused) this.worlds[idx]?.startSimulation();
        });

        // The selected world may be an offspring whose ruleset just changed; reconcile its UI unless
        // it is a parent (untouched).
        if (!this._isParent(this.selectedWorldIndex)) {
            this.dispatchSelectedWorldUpdates();
        }
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        EventBus.dispatch(EVENTS.ALL_WORLDS_RESET);
        EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
            message: `Bred ${offspringIndices.length} offspring from ${parentHexes.length} parent${parentHexes.length === 1 ? '' : 's'}`
        });
    };

    /**
     * Toggle a world's breeding-parent flag (defaults to the selected world). A parent is a source
     * for the genepool breed and is never overwritten by it. Returns the new flag (for a toast).
     * @param {number} [worldIndex] - Defaults to the selected world.
     * @returns {boolean}
     */
    toggleWorldParent = (worldIndex = this.selectedWorldIndex) => {
        const settings = this.worldSettings[worldIndex];
        if (!settings) return false;
        settings.isParent = !settings.isParent;
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        return settings.isParent;
    };

    _cloneRuleset = () => {
        const selectedProxy = this.worlds[this.selectedWorldIndex];
        if (!selectedProxy) {
            console.error("Cannot clone: selected world proxy is not available.");
            return;
        }

        const sourceRulesetHex = this.getCurrentRulesetHex();
        if (sourceRulesetHex === "Error" || sourceRulesetHex === "N/A") {
             console.error("Cannot clone: selected world's ruleset is invalid.");
             EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: "Selected world has an invalid ruleset and cannot be cloned.", type: 'error' });
             return;
        }

        const baseSeed = Date.now();
        this.worlds.forEach((proxy, idx) => {
            // A locked world keeps its ruleset (and grid) untouched by clone.
            if (idx !== this.selectedWorldIndex && this._isLocked(idx)) return;
            // The selected world already runs the source ruleset, so skip its worker upload.
            this._commitRuleset(idx, sourceRulesetHex, {
                uploadToWorker: idx !== this.selectedWorldIndex,
                reset: true,
                seed: this._getResetSeed(baseSeed, idx),
            });

            if (!this.isGloballyPaused) {
                proxy.startSimulation();
            }
        });

        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        EventBus.dispatch(EVENTS.ALL_WORLDS_RESET); 
    }

    _modifyRulesetForScope = (scope, modifierFunc, conditionalResetScope) => {
        const indices = this._getAffectedWorldIndices(scope);
        const baseSeed = Date.now();
        indices.forEach(idx => {
            const proxyStats = this.worlds[idx]?.getLatestStats();
            let currentHex = (proxyStats?.rulesetHex && proxyStats.rulesetHex !== "Error")
                ? proxyStats.rulesetHex
                : this.worldSettings[idx]?.rulesetHex;

            if (!currentHex) {
                console.warn(`_modifyRulesetForScope: No current hex for world ${idx}, skipping modification.`);
                return;
            }

            const newHex = modifierFunc(currentHex);

            if (newHex !== currentHex && newHex !== "Error") {
                const shouldReset = conditionalResetScope !== 'none' &&
                    this._getAffectedWorldIndices(conditionalResetScope).includes(idx);
                this._commitRuleset(idx, newHex, {
                    reset: shouldReset,
                    seed: this._getResetSeed(baseSeed, idx),
                });
            }
        });

        if (indices.includes(this.selectedWorldIndex)) {
            this.dispatchSelectedWorldUpdates();
            if (this.worldSettings[this.selectedWorldIndex]) {
                PersistenceService.saveRuleset(this.worldSettings[this.selectedWorldIndex].rulesetHex);
            }
        }
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
    }

    setGlobalPause = (isPaused) => {
        // Resuming play from a scrubbed-back frame: leave scrub mode so ticking continues forward from
        // the viewed frame (the worker also self-heals on START_SIMULATION, but clear our state here).
        // exitScrub is a no-op when not scrubbing, so the unconditional call matches the prior guard.
        if (!isPaused) this.scrubHistoryController.exitScrub();
        this.isGloballyPaused = isPaused;
        this.worlds.forEach(proxy => {
            if (this.isGloballyPaused || !proxy.getLatestStats().isEnabled) {
                proxy.stopSimulation();
            } else {
                proxy.startSimulation();
                proxy.setSpeed(this.simulationController.getSpeed());
            }
        });
        this.simulationController._syncPauseState(this.isGloballyPaused);
        // Respect global pause during exploration: the pause button pauses/resumes the search loop.
        // (Guarded so the restore path — which runs after the service is already idle — can't recurse.)
        if (this.autoExploreService?.isRunning()) {
            if (this.isGloballyPaused) this.autoExploreService.pause();
            else this.autoExploreService.resume();
        }
    }

    // --- Auto-explore / measurement session (owned by ExploreSessionCoordinator) -------------
    // The AutoExploreService owns the search loop but mutates worlds through these helpers so the
    // proxy/persistence dance stays out of the loop; the snapshot/restore pair brackets a whole
    // session. The bodies live in ExploreSessionCoordinator now; these thin delegators preserve the
    // surface AutoExploreService calls (and that the determinism/target tests mock on a fake wm), and
    // the capture providers passed to AutoExploreService above.
    _captureAutoExploreSnapshot = () => this.exploreSessionCoordinator._captureAutoExploreSnapshot();
    _setAllWorldsEnabledForExplore = (enabled) => this.exploreSessionCoordinator._setAllWorldsEnabledForExplore(enabled);
    _applyExploreRuleset = (worldIndex, hex) => this.exploreSessionCoordinator._applyExploreRuleset(worldIndex, hex);
    _captureExploreThumbnail = (worldIndex) => this.exploreSessionCoordinator._captureExploreThumbnail(worldIndex);
    _restoreAutoExploreSnapshot = (snapshot, opts) => this.exploreSessionCoordinator._restoreAutoExploreSnapshot(snapshot, opts);

    setGlobalSpeed = (speed) => {
        const newSpeed = Math.max(1, Math.min(Config.MAX_SIM_SPEED, speed));
        this.worlds.forEach(proxy => proxy.setSpeed(newSpeed));
        
    }

    getWorldsRenderData = () => {
        return this.worlds.map(proxy => proxy.getLatestRenderData());
    }

    getWorldsFullStatus = () => {
        return this.worlds.map(proxy => proxy.getFullStatus());
    }

    // Called by the renderer once a world's FBO has been redrawn, so the next
    // frame can skip the redraw until something visual changes again.
    clearWorldRenderDirty = (worldIndex) => {
        this.worlds[worldIndex]?.clearRenderDirty();
    }

    // Force every world's FBO to be redrawn on the next frame (e.g. after a color
    // LUT change that alters appearance without producing a STATE_UPDATE).
    markAllWorldsRenderDirty = () => {
        this.worlds.forEach(proxy => proxy?.markRenderDirty());
    }


    getSelectedWorldIndex = () => this.selectedWorldIndex;

    // Returns the selected world's live per-cell binary state buffer (Uint8Array of
    // NUM_CELLS, 0/1), or null if not yet available. Used by pattern capture to read
    // which cells are active within a selected region — same view the renderer reads.
    getSelectedWorldStateArray = () => this.worlds[this.selectedWorldIndex]?.latestStateArray || null;

    getCurrentRulesetHex = () => {
        const proxy = this.worlds[this.selectedWorldIndex];
        if (proxy && proxy.isInitialized) {
            const stats = proxy.getLatestStats();
            if (stats.rulesetHex && stats.rulesetHex !== "Error") {
                return stats.rulesetHex;
            }
        }
        return this.worldSettings[this.selectedWorldIndex]?.rulesetHex || "N/A";
    }
    getCurrentRulesetArray = () => hexToRuleset(this.getCurrentRulesetHex());

    getSelectedWorldStats = () => {
        const proxy = this.worlds[this.selectedWorldIndex];
        return proxy ? proxy.getLatestStats() : { tick: 0, ratio: 0, entropy: 0, isEnabled: false, rulesetHex: "0".repeat(32), tps: 0, ruleUsage: new Uint32Array(128) };
    }
    getWorldSettingsForUI = () => this.worldSettings.map(ws => ({
        initialState: ws.initialState,
        enabled: ws.enabled,
        locked: !!ws.locked,
        isParent: !!ws.isParent,
        rulesetHex: ws.rulesetHex
    }));

    getSymmetryData = () => this.symmetryData;

    // Parse the current ruleset hex into its 128-entry array, memoizing on the hex string. This is
    // called in tight loops (editor grids iterate every center-state × neighbor-count combination),
    // and re-parsing the same hex each call was pure waste.
    _getParsedCurrentRuleset = () => {
        const currentHex = this.getCurrentRulesetHex();
        if (currentHex === "N/A" || currentHex === "Error") return null;
        if (this._parsedRulesetCache && this._parsedRulesetCache.hex === currentHex) {
            return this._parsedRulesetCache.ruleset;
        }
        const ruleset = hexToRuleset(currentHex);
        if (!ruleset || ruleset.length !== 128) return null;
        this._parsedRulesetCache = { hex: currentHex, ruleset };
        return ruleset;
    }

    getEffectiveRuleForNeighborCount = (centerState, numActiveNeighbors) =>
        RulesetService.getEffectiveRuleForNeighborCount(this._getParsedCurrentRuleset(), centerState, numActiveNeighbors);

    getEffectiveRuleForTotalisticSum = (sum) =>
        RulesetService.getEffectiveRuleForTotalisticSum(this._getParsedCurrentRuleset(), sum);

    getCanonicalRuleDetails = (mode = 'r_sym') =>
        this.rulesetService.getCanonicalRuleDetails(this._getParsedCurrentRuleset(), mode);

    _generateRandomRulesetHex = (bias, generationMode) =>
        this.rulesetService.generateRandomRulesetHex(bias, generationMode);

    saveSelectedWorldState = () => {
        const proxy = this.worlds[this.selectedWorldIndex];
        if (!proxy) return;

        const stateArray = proxy.latestStateArray || new Uint8Array(0);
        const rulesetHex = this.getCurrentRulesetHex();
        const stats = proxy.getLatestStats();

        const data = {
            rows: Config.GRID_ROWS,
            cols: Config.GRID_COLS,
            rulesetHex: rulesetHex,
            // v2 format: per-cell bytes base64-encoded (~4× smaller than a JSON number
            // array). loadWorldState still reads the legacy `state: number[]` field.
            format: 'b64',
            stateB64: cellsToBase64(stateArray),
            worldTick: stats.tick
        };
        EventBus.dispatch(EVENTS.TRIGGER_DOWNLOAD, {
            filename: `hex_state_world${this.selectedWorldIndex}_${rulesetHex}_${new Date().toISOString().slice(0, -4).replace(/[:.-]/g, '')}.json`,
            content: JSON.stringify(data, null, 2),
            mimeType: 'application/json'
        });
    }

    // The Analysis panel's on-demand non-destructive measurement now lives in
    // ExploreSessionCoordinator; this delegator preserves the surface AnalysisComponent calls.
    measureSelectedWorld = (opts) => this.exploreSessionCoordinator.measureSelectedWorld(opts);

    // Ruleset Library thumbnail baking is owned by ThumbnailBakeService (borrow-and-restore on a
    // scratch world). These thin delegators preserve the public API used by SaveRulesetModal,
    // RulesetLibraryComponent, and LibraryCurationOverlay.
    bakeThumbnail = (job) => this.thumbnailBakeService.bakeThumbnail(job);
    bakeThumbnails = (jobs = []) => this.thumbnailBakeService.bakeThumbnails(jobs);
    backfillMissingThumbnails = (entries, ctx) => this.thumbnailBakeService.backfillMissingThumbnails(entries, ctx);

    /**
     * Toggle the selected world's ruleset lock. A locked world keeps its ruleset through the
     * evolutionary/automatic paths (Generate, Mutate, Clone, Clone & Mutate, Breed); deliberate
     * sets/edits still apply. Returns the new locked state (for the caller's toast).
     * @returns {boolean}
     */
    toggleSelectedWorldLock = () => {
        const idx = this.selectedWorldIndex;
        const settings = this.worldSettings[idx];
        if (!settings) return false;
        settings.locked = !settings.locked;
        PersistenceService.saveWorldSettings(this.worldSettings);
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        return settings.locked;
    };

    /**
     * Copy a world's current cell state (NOT its ruleset) onto another world; the target keeps
     * its own ruleset. The copied state is stamped at tick 0 (a fresh paste, not a continuation).
     * @param {number} sourceIndex
     * @param {number} targetIndex
     */
    copyWorldState = (sourceIndex, targetIndex) => {
        if (sourceIndex === targetIndex) return;
        const source = this.worlds[sourceIndex];
        const target = this.worlds[targetIndex];
        if (!source || !target) return;

        const stateArray = source.latestStateArray;
        if (!stateArray || stateArray.length !== Config.NUM_CELLS) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'Source world state is not ready yet.', type: 'error' });
            return;
        }

        const targetRulesetHex = this._getRulesetHexForWorld(targetIndex);
        const targetRulesetArray = hexToRuleset(targetRulesetHex);
        const stateCopy = new Uint8Array(stateArray);

        target.sendCommand('LOAD_STATE', {
            newStateBuffer: stateCopy.buffer.slice(0),
            newRulesetBuffer: targetRulesetArray.buffer.slice(0),
            worldTick: 0,
        }, [stateCopy.buffer.slice(0), targetRulesetArray.buffer.slice(0)]);

        target.setEnabled(true);
        if (this.worldSettings[targetIndex]) this.worldSettings[targetIndex].enabled = true;
        if (!this.isGloballyPaused) {
            target.startSimulation();
            target.setSpeed(this.simulationController.getSpeed());
        }

        PersistenceService.saveWorldSettings(this.worldSettings);
        if (targetIndex === this.selectedWorldIndex) this.dispatchSelectedWorldUpdates();
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: `Copied world ${sourceIndex + 1} state → world ${targetIndex + 1}` });
    };

    /**
     * Freeze the selected world's current cells into the Saved Starts library and (optionally) make
     * that grid the world's — or every world's — initial state, so a plain reset replays it exactly.
     *
     * Non-destructive: nothing is reset, paused, or overwritten; only the *initial-state config*
     * changes. The entry embeds its own cell payload, so it survives deletion from the library.
     * @param {'selected'|'all'|'none'} [assignScope='selected']
     * @returns {object|null} The stored entry (the pre-existing one when deduped), or null on refusal.
     */
    captureStateToLibrary = (assignScope = 'selected') => {
        const idx = this.selectedWorldIndex;
        const proxy = this.worlds[idx];
        const stateArray = proxy?.latestStateArray;
        if (!stateArray || stateArray.length !== Config.NUM_CELLS) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'World state is not ready yet.', type: 'error' });
            return null;
        }

        const rulesetHex = this._getRulesetHexForWorld(idx);
        const tick = proxy.getLatestStats()?.tick ?? 0;
        let liveCells = 0;
        for (let i = 0; i < stateArray.length; i++) liveCells += stateArray[i] ? 1 : 0;

        const candidate = {
            id: (globalThis.crypto?.randomUUID?.() ?? `ss-${Date.now()}-${Math.floor(Math.random() * 1e9)}`),
            name: `${rulesetName(rulesetHex)} @ ${tick}`,
            rows: Config.GRID_ROWS,
            cols: Config.GRID_COLS,
            stateB64: cellsToBase64(stateArray),
            density: liveCells / (stateArray.length || 1),
            rulesetHex,
            capturedTick: tick,
            createdAt: Date.now(),
        };

        const { entry, deduped, error } = stateLibraryService.add(candidate);
        if (!entry) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: error || 'Could not save this start.', type: 'error' });
            return null;
        }

        const initialState = stateLibraryService.buildInitialState(entry);
        let assignmentNote = 'added to the library';
        if (assignScope === 'selected' || assignScope === 'all') {
            this.worldSettings[idx].initialState = initialState;
            if (assignScope === 'all') {
                // Deep-clones the selected world's initial state onto all nine, persists, dispatches.
                this._applySelectedInitialStateToAll();
                assignmentNote = 'set as the start for all worlds (R resets to it)';
            } else {
                PersistenceService.saveWorldSettings(this.worldSettings);
                EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
                assignmentNote = `set as world ${idx + 1}'s start (R resets to it)`;
            }
        }

        EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
            message: `${deduped ? 'Already saved as' : 'Saved start'} "${entry.name}" — ${assignmentNote}`,
        });
        return entry;
    };

    loadWorldState = (worldIndex, loadedData) => {
        if (worldIndex < 0 || worldIndex >= this.worlds.length) return;
        // Shape/decode validation (both the v2 base64 and the legacy number-array format) is shared
        // with the toolbar's file input and the Saved Starts importer.
        const parsed = parseStateFile(loadedData);
        if (parsed.error) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: parsed.error, type: 'error' });
            return;
        }
        if (parsed.rows !== Config.GRID_ROWS || parsed.cols !== Config.GRID_COLS) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: "Grid dimensions in file do not match current configuration.", type: 'error' });
            return;
        }
        const proxy = this.worlds[worldIndex];
        const newRulesetArray = hexToRuleset(parsed.rulesetHex);
        const newStateArray = parsed.cells;

        if (this.worldSettings[worldIndex]) {
            // The ruleset reaches the worker via the LOAD_STATE command below, not setRuleset.
            this._commitRuleset(worldIndex, parsed.rulesetHex, { uploadToWorker: false });
            const newDensity = newStateArray.reduce((sum, val) => sum + val, 0) / (newStateArray.length || 1);
            this.worldSettings[worldIndex].initialState = {
                mode: 'density',
                params: { density: newDensity }
            };
            this.worldSettings[worldIndex].enabled = true;
        }

        proxy.sendCommand('LOAD_STATE', {
            newStateBuffer: newStateArray.buffer.slice(0),
            newRulesetBuffer: newRulesetArray.buffer.slice(0),
            worldTick: parsed.worldTick
        }, [newStateArray.buffer.slice(0), newRulesetArray.buffer.slice(0)]);

        proxy.setEnabled(true);
                    if (!this.isGloballyPaused) {
                proxy.startSimulation();
                proxy.setSpeed(this.simulationController.getSpeed());
            }

        PersistenceService.saveWorldSettings(this.worldSettings);
        if (worldIndex === this.selectedWorldIndex) {
            this.dispatchSelectedWorldUpdates();
        }
        EventBus.dispatch(EVENTS.WORLD_SETTINGS_CHANGED, this.getWorldSettingsForUI());
        EventBus.dispatch(EVENTS.ALL_WORLDS_RESET);
    }

    revertToHistoryState = (worldIndex, historyIndex) => {
        const settings = this.worldSettings[worldIndex];
        if (!settings || historyIndex < 0 || historyIndex >= settings.rulesetHistory.length) return;

        const currentIndex = settings.rulesetHistory.length - 1;
        if (historyIndex === currentIndex) return; 

        
        const itemsToMove = settings.rulesetHistory.splice(historyIndex + 1);
        settings.rulesetFuture.unshift(...itemsToMove.reverse());

        const targetRuleset = settings.rulesetHistory[historyIndex];
        this._commitRuleset(worldIndex, targetRuleset, { addToHistory: false });

        if (worldIndex === this.selectedWorldIndex) {
            EventBus.dispatch(EVENTS.RULESET_CHANGED, targetRuleset);
            PersistenceService.saveRuleset(targetRuleset);
        }
        EventBus.dispatch(EVENTS.HISTORY_CHANGED, { worldIndex });
        PersistenceService.saveWorldSettings(this.worldSettings);
    }

    undoRulesetChange = (worldIndex) => {
        const settings = this.worldSettings[worldIndex];
        if (!settings || settings.rulesetHistory.length < 2) return;

        
        const targetIndex = settings.rulesetHistory.length - 2;
        
        const currentRuleset = settings.rulesetHistory.pop();
        settings.rulesetFuture.unshift(currentRuleset); 

        const previousRuleset = settings.rulesetHistory[targetIndex];
        this._commitRuleset(worldIndex, previousRuleset, { addToHistory: false });

        if (worldIndex === this.selectedWorldIndex) {
            EventBus.dispatch(EVENTS.RULESET_CHANGED, previousRuleset);
            PersistenceService.saveRuleset(previousRuleset);
        }
        EventBus.dispatch(EVENTS.HISTORY_CHANGED, { worldIndex });
        PersistenceService.saveWorldSettings(this.worldSettings);
    }

    redoRulesetChange = (worldIndex) => {
        const settings = this.worldSettings[worldIndex];
        if (!settings || settings.rulesetFuture.length === 0) return;

        const nextRuleset = settings.rulesetFuture.shift();
        settings.rulesetHistory.push(nextRuleset);
        // History already advanced above; just upload + sync the cached hex.
        this._commitRuleset(worldIndex, nextRuleset, { addToHistory: false });

        if (worldIndex === this.selectedWorldIndex) {
            EventBus.dispatch(EVENTS.RULESET_CHANGED, nextRuleset);
            PersistenceService.saveRuleset(nextRuleset);
        }
        EventBus.dispatch(EVENTS.HISTORY_CHANGED, { worldIndex });
        PersistenceService.saveWorldSettings(this.worldSettings);
    }

    getRulesetHistoryArrays = (worldIndex) => {
        const settings = this.worldSettings[worldIndex];
        if (!settings) return { history: [], future: [] };
        return {
            history: [...settings.rulesetHistory], 
            future: [...settings.rulesetFuture],
        };
    }

    getEntropySamplingState = () => {
        return { enabled: this.isEntropySamplingEnabled, rate: this.entropySampleRate };
    }

    /**
     * Freeze the selected world into a portable **world code** (`HXW1.…`, see {@link WorldCodec}) —
     * the payload the Reddit/Devvit app turns into a post (#26 Phase 2).
     *
     * A share link is a *recipe* (ruleset + density + seed); this is the **dish**: the exact grid, the
     * exact live cells as they are on screen right now, and the palette. Nothing about it is
     * re-derived on the other side, so a post shows the world the author saw — not a statistically
     * similar one — and no future change to the density generators can retroactively rewrite it.
     *
     * The current cells are the code's *initial* state: the post opens paused on them and evolves
     * from there when the viewer hits play.
     *
     * **One exception — a pristine random-fill/clumps world ships its recipe, not its cells.** If the
     * selected world is sitting on its freshly generated initial state (tick 0) and that state came
     * from the `density` ("random fill") or `clusters` ("clumps") generator, the code carries the
     * generator's params instead of one frozen draw. The post then re-rolls a new arrangement on every
     * start — including the in-post reset button — which is the whole point of a random specimen. An
     * evolved or hand-drawn world has no such recipe, so it freezes its exact cells as before.
     *
     * The palette travels as the *settings* (mode, preset, custom maps, flicker flag, hue shift), not
     * as a baked table: the embed recomputes the symmetry tables itself, so it can rebuild the exact
     * same LUT from a few dozen compressible bytes instead of 768 near-random ones.
     *
     * @param {object} colorSettings ColorController.getSettings().
     * @returns {Promise<string|null>} The code, or null if the world's cells aren't available yet.
     */
    exportWorldCode = async (colorSettings) => {
        const idx = this.selectedWorldIndex;
        const proxy = this.worlds[idx];
        const cells = proxy?.latestStateArray;
        if (!cells || cells.length !== Config.NUM_CELLS) return null;

        const base = {
            rows: Config.GRID_ROWS,
            cols: Config.GRID_COLS,
            rulesetHex: this._getRulesetHexForWorld(idx),
            colorSettings,
            speed: this.simulationController.getSpeed(),
            brushSize: this.brushController?.getBrushSize?.() ?? Config.DEFAULT_NEIGHBORHOOD_SIZE,
        };

        const initialState = this.worldSettings[idx]?.initialState;
        const isGeneratorMode = initialState
            && (initialState.mode === 'density' || initialState.mode === 'clusters');
        // Only when the world hasn't evolved past its generated initial state — otherwise the cells on
        // screen are a specific frame, not a representative of the recipe.
        if (isGeneratorMode && proxy.getLatestStats().tick === 0) {
            return encodeWorldCode({
                ...base,
                generator: { mode: initialState.mode, params: initialState.params },
            });
        }

        return encodeWorldCode({ ...base, cells });
    };

    generateShareUrl({ includeWorldState = false } = {}) {
        return ShareCodec.encode({
            worldSettings: this.getWorldSettingsForUI(),
            selectedWorldIndex: this.getSelectedWorldIndex(),
            camera: this.getCurrentCameraState(),
            gridRows: Config.GRID_ROWS,
            origin: window.location.origin,
            pathname: window.location.pathname,
            includeWorldState,
        });
    }

    _invertSelectedRuleset = () => {
        const selectedIndex = this.selectedWorldIndex;
        const currentHex = this.worldSettings[selectedIndex]?.rulesetHex;

        if (!currentHex || currentHex === "Error" || currentHex === "N/A") {
            console.error("Cannot invert ruleset: No valid ruleset on selected world.");
            return;
        }

        const invertedHex = RulesetService.invertHex(currentHex);
        this.#applyRulesetToWorlds(invertedHex, 'selected', false);
    }

    areAllWorkersInitialized = () => {
        return this.worlds.length > 0 && this.worlds.every(proxy => proxy && proxy.isInitialized);
    }

    terminateAllWorkers = () => {
        this.worlds.forEach(proxy => proxy.terminate());
    }

    _initCameraStates(sharedCameraSettings) {
        const gridCenter = getGridCenterWorld();
        this.cameraStates.push(...createInitialCameraStates(
            Config.NUM_WORLDS,
            gridCenter,
            this.selectedWorldIndex,
            sharedCameraSettings,
        ));
    }

    /**
     * Return the selected world's camera to the whole-grid framing (the "Reset view" affordance —
     * roadmap #31). Announced on the bus because the camera object is mutated in place, so nothing
     * else can observe the change.
     */
    resetSelectedCamera = () => {
        const camera = this.cameraStates[this.selectedWorldIndex];
        if (!camera) return;
        const gridCenter = getGridCenterWorld();
        camera.x = gridCenter.x;
        camera.y = gridCenter.y;
        camera.zoom = 1.0;
        EventBus.dispatch(EVENTS.CAMERA_CHANGED, { zoom: camera.zoom, worldIndex: this.selectedWorldIndex });
    }

    getCurrentCameraState = () => {
        return this.cameraStates[this.selectedWorldIndex];
    }
}
